import { GetFilesProps } from '@/hooks/useFiles';
import { useAuth } from '@/state/auth';
import { GraphQuery, MyResourceKey, UniqueQueryKeys } from '@/types';

export const getQueryKey = {
  getAgents: (variant?: 'basic' | 'enhanced') =>
    [UniqueQueryKeys.AGENTS, ...(variant ? [variant] : [])] as const,
  getEnrich: (type: string, id: string) => [UniqueQueryKeys.ENRICH, type, id],
  getMy: (
    key: MyResourceKey,
    compositeKey?: string,
    to?: string,
    desc?: boolean,
    global?: boolean,
    allTenants?: boolean,
    osint?: boolean,
    praetorian?: boolean
  ) => {
    return [
      UniqueQueryKeys.MY,
      key,
      ...(compositeKey ? [compositeKey] : []),
      ...(to ? [to] : []),
      ...(desc ? [desc] : []),
      ...(global ? [global] : []),
      ...(allTenants ? [allTenants] : []),
      ...(osint ? [osint] : []),
      ...(praetorian ? [praetorian] : []),
    ];
  },
  getAccountAlerts: (account: string) => [
    UniqueQueryKeys.ACCOUNT_ALERTS,
    account,
  ],
  getBackend: () => [UniqueQueryKeys.Backends],
  getFile: (props: GetFilesProps) => [
    UniqueQueryKeys.GET_FILE,
    props.name,
    props.global,
  ],
  genericSearch: (
    search: string,
    desc?: boolean,
    global?: boolean,
    email?: string,
    allTenants?: boolean
  ) => [
    UniqueQueryKeys.GENERIC_MY_SEARCH,
    ...(search ? [search] : []),
    ...(desc !== undefined ? [String(desc)] : []),
    ...(global ? [global] : []),
    ...(email ? [email] : []),
    ...(allTenants ? [allTenants] : []),
  ],
  getCounts: (
    resource: MyResourceKey,
    compositeKey?: string,
    email?: string
  ) => [
    UniqueQueryKeys.COUNTS,
    resource,
    ...(compositeKey ? [compositeKey] : []),
    ...(email ? [email] : []),
  ],
  getGavatarProfilePicture: (email: string) => [
    UniqueQueryKeys.GAVATAR_PROFILE_PICTURE,
    email,
  ],
  getKev: () => [UniqueQueryKeys.KEV],
  userAttributes: () => [UniqueQueryKeys.USER_ATTRIBUTES],
  getSeedFilters: ({
    type,
    column,
  }: {
    type: 'domain' | 'ip';
    column: string;
  }) => [UniqueQueryKeys.SEED_FILTERS, type, column],
  getGraphQuery: (query: GraphQuery) => [UniqueQueryKeys.GRAPH_QUERY, query],
  getCapabilities: (filters?: {
    name?: string;
    target?: string;
    executor?: string;
  }) => [
    UniqueQueryKeys.CAPABILITIES,
    ...(filters ? [JSON.stringify(filters)] : []),
  ],
  getApiKeys: () => [UniqueQueryKeys.API_KEYS],
  getPlextracReports: (customerEmail?: string) => [
    UniqueQueryKeys.PLEXTAC_REPORTS,
    ...(customerEmail ? [customerEmail] : []),
  ],
  getReporting: (clientId?: string, customerEmail?: string) => [
    UniqueQueryKeys.REPORTING,
    ...(clientId ? [clientId] : []),
    ...(customerEmail ? [customerEmail] : []),
  ],
  getEvidenceFiles: (riskKey: string) => [
    UniqueQueryKeys.EVIDENCE_FILES,
    riskKey,
  ],
  getCollaboratorQuery: (key: string, email: string) => [
    UniqueQueryKeys.COLLABORATOR_QUERY,
    key,
    email,
  ],
  getPlexTracClientId: (customerEmail?: string) => [
    UniqueQueryKeys.PLEXTAC_CLIENT_ID,
    ...(customerEmail ? [customerEmail] : []),
  ],
  getAegisManagementTasks: (agentId?: string) => [
    UniqueQueryKeys.AEGIS_MANAGEMENT_TASKS,
    ...(agentId ? [agentId] : []),
  ],
  getAIBudget: () => [UniqueQueryKeys.AI_BUDGET],
  getConversationCost: (conversationId: string) => [
    UniqueQueryKeys.CONVERSATION_COST,
    conversationId,
  ],
  getHuntCost: (huntUuid: string) => [UniqueQueryKeys.HUNT_COST, huntUuid],
};

export function useGetUserKey(
  key: ReadonlyArray<unknown> = [],
  doNotImpersonate?: boolean,
  email?: string
) {
  const { friend } = useAuth();

  if (!doNotImpersonate && friend) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return [...key, friend] as any;
  }

  if (email) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return [...key, email] as any;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return [...key] as any;
}
